import { useFormContext } from 'react-hook-form';
import { LIMIT } from '@anytag/shared-constants';
import { useQueryHelper } from '@anytag/shared-hooks';
import { getValuesArray } from '@anytag/shared-atoms';
import { useAllCampaignsForSearchLazyQuery } from '@anytag/graphql';
import { getCampaignsFromUrlString, getOffset } from '@anytag/shared-utils';
import { SelectValueType } from '../selector-field';
import { AsyncSelectorSearchField, AsyncSelectorSearchFieldProps } from '../async-selector-search-field';
import {
  CampaignSearchOption,
  AsyncSearchCampaignFieldProps,
  SingleAsyncSearchCampaignFieldProps,
  MultipleAsyncSearchCampaignFieldProps
} from './types';

// Function overloads for proper type inference based on `multiple` prop
export function AsyncSearchCampaignField<T extends string>(
  props: MultipleAsyncSearchCampaignFieldProps<T>
): React.ReactElement;
export function AsyncSearchCampaignField<T extends string>(
  props: SingleAsyncSearchCampaignFieldProps<T>
): React.ReactElement;
export function AsyncSearchCampaignField<T extends string>({
  name,
  multiple,
  page = 1,
  searchProps,
  ...restProps
}: AsyncSearchCampaignFieldProps<T>): React.ReactElement {
  const { t, enqueueSnackbar } = useQueryHelper();
  const { getAllCampaignsForSearch, loading } = useAllCampaignsForSearchLazyQuery();
  const { watch } = useFormContext<{ [name: string]: SelectValueType<CampaignSearchOption> }>();

  const value = watch(name);
  const selectedCampaigns = getValuesArray({ value, multiple }) as string[]; // use string[] internally for comparisons

  const fetchCampaigns: AsyncSelectorSearchFieldProps<T, CampaignSearchOption>['onSearch'] = async (
    keyword,
    initialLoad
  ) => {
    let campaigns: CampaignSearchOption[] = [];

    try {
      const { data } = await getAllCampaignsForSearch({
        variables: initialLoad
          ? {
              offset: 0,
              keyword: '',
              limit: selectedCampaigns.length,
              // TODO: BE is return wrong data for campaigns filter, we need to fix it
              campaigns: getCampaignsFromUrlString(selectedCampaigns)
            }
          : { keyword, limit: LIMIT, offset: getOffset(page, LIMIT) }
      });
      campaigns =
        data?.allCampaignsForSearch?.map<CampaignSearchOption>((campaign) => ({
          label: campaign.title,
          value: `${campaign.type.valueOf().toLowerCase()}/${campaign.id}`
        })) || [];
    } catch (error) {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    }

    return campaigns;
  };

  const commonProps: Pick<
    AsyncSelectorSearchFieldProps<T, CampaignSearchOption>,
    'name' | 'loading' | 'onSearch' | 'placeholder' | 'searchProps'
  > = {
    name,
    loading,
    onSearch: fetchCampaigns,
    placeholder: t<string>('Selector.Select campaigns'),
    searchProps: { placeholder: t<string>('Search.EngagementFilter'), ...searchProps }
  };

  return multiple ? (
    <AsyncSelectorSearchField<T, CampaignSearchOption>
      {...(restProps as Partial<MultipleAsyncSearchCampaignFieldProps<T>>)}
      {...commonProps}
      multiple={true}
    />
  ) : (
    <AsyncSelectorSearchField<T, CampaignSearchOption>
      {...(restProps as Partial<SingleAsyncSearchCampaignFieldProps<T>>)}
      {...commonProps}
      multiple={false}
    />
  );
}
